import React, { useState, useEffect } from 'react';
import { createApiUrl } from '../utils/apiConfig';
import axios from 'axios';
import styles from './UnifiedStudentProgress.module.css';

const UnifiedStudentProgress = ({ studentId, onClose }) => {
  
  const [student, setStudent] = useState(null);
  const [progressData, setProgressData] = useState({
    missions: [],
    lessons: [],
    quizzes: [],
    overallStats: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [quizResponseData, setQuizResponseData] = useState({});

  useEffect(() => {
    if (studentId) {
      fetchStudentProgress();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run when studentId changes
  }, [studentId]);

  // Fetch quiz response data when a quiz is selected for viewing
  useEffect(() => {
    if (selectedActivity && selectedActivity.type === 'quiz' &&
        (selectedActivity.status === 'completed' || selectedActivity.status === 'graded')) {
      fetchQuizResponseData(selectedActivity._id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run when selectedActivity changes
  }, [selectedActivity]);

  const fetchStudentProgress = async () => {
    try {
      setLoading(true);
      
      // Use the existing endpoint that was working before
      const response = await axios.get(createApiUrl(`/api/students/${studentId}/progress`), {
        withCredentials: true
      });
      
      if (response.data.success) {
        setStudent(response.data.data.student);
        setProgressData(response.data.data);
      }
    } catch (err) {
      // console.error('Error fetching student progress:', err);
      setError('Failed to load student progress');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuizResponseData = async (quizId) => {
    try {
      
      // Find the quiz data to get assignmentId (like student dashboard does)
      const quizData = progressData.quizzes.find(quiz => quiz._id === quizId);
      
      // Check if we already have student responses in progressData
      if (quizData && quizData.studentResponses && quizData.studentResponses.length > 0) {
        
        // Convert studentResponses array to object format
        const responses = {};
        quizData.studentResponses.forEach(response => {
          responses[response.questionId] = response.answer;
        });
        
        // Convert essayResponses if available
        const essayResponses = {};
        if (quizData.essayResponses && quizData.essayResponses.length > 0) {
          quizData.essayResponses.forEach(response => {
            essayResponses[response.questionId] = response.response;
          });
        }
        
        setQuizResponseData(prev => ({
          ...prev,
          [quizId]: {
            responses: responses,
            essayResponses: essayResponses,
            percentage: quizData.percentage || 0,
            totalScore: quizData.totalScore || 0,
            status: quizData.status || 'completed'
          }
        }));
        
        return;
      }
      
      // Use the exact same approach as student dashboard viewQuizResults
      const [progressResponse, assignmentResponse] = await Promise.all([
        axios.get(createApiUrl(`/api/quiz/${quizId}/progress`), { withCredentials: true }),
        axios.get(createApiUrl(`/api/quiz-assignments/student/${studentId}`), { withCredentials: true })
      ]);
      
      if (progressResponse.data.success && progressResponse.data.data) {
        const resultData = progressResponse.data.data;
        
        // Combine regular responses and essay responses exactly like student dashboard
        const allResponses = { ...(resultData.responses || {}), ...(resultData.essayResponses || {}) };
        
        // Get the latest assignment data with essay grades (exactly like student dashboard)
        let essayGrades = {};
        if (assignmentResponse.data.success && assignmentResponse.data.assignments) {
          
          const assignmentData = assignmentResponse.data.assignments.find(assignment => 
            assignment.quizId._id === quizId || assignment._id === quizData?.assignmentId
          );
          if (assignmentData) {
            essayGrades = assignmentData.essayGrades || {};
          } else {
            // Continue
          }
        } else {
          // return
        }
        
        setQuizResponseData(prev => ({
          ...prev,
          [quizId]: {
            responses: allResponses,
            essayGrades: essayGrades,
            percentage: resultData.percentage || 0,
            totalScore: resultData.totalScore || 0,
            status: resultData.status || 'completed'
          }
        }));
      } else {
        // continue
      }
    } catch (error) {
      console.error(`Error fetching quiz response data for quiz ${quizId}:`, error);
    }
  };


  const getActivityStatus = (activity) => {
    // Handle different status values that might come from the backend
    const status = activity.status || activity.assignmentStatus;
    
    if (status === 'completed') return { variant: 'success', text: 'Completed' };
    if (status === 'in-progress') return { variant: 'warning', text: 'In Progress' };
    if (status === 'graded') return { variant: 'info', text: 'Graded' };
    if (status === 'overdue') return { variant: 'danger', text: 'Overdue' };
    if (status === 'assigned') return { variant: 'secondary', text: 'Assigned' };
    if (status === 'not-started') return { variant: 'secondary', text: 'Not Started' };
    
    // Default fallback
    return { variant: 'secondary', text: 'Not Started' };
  };

  const getGradeColor = (percentage) => {
    if (percentage >= 90) return 'text-success';
    if (percentage >= 80) return 'text-primary';
    if (percentage >= 70) return 'text-warning';
    return 'text-danger';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const calculateOverallProgress = () => {
    const allActivities = [...progressData.missions, ...progressData.lessons, ...progressData.quizzes];
    const completed = allActivities.filter(a => {
      const status = a.status || a.assignmentStatus;
      return status === 'completed' || status === 'graded';
    }).length;
    const total = allActivities.length;
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const getActivityTypeIcon = (type) => {
    const icons = {
      mission: '🚀',
      lesson: '📚',
      quiz: '❓'
    };
    return icons[type] || '📝';
  };

  const getActivityTypeColor = (type) => {
    const colors = {
      mission: 'primary',
      lesson: 'success',
      quiz: 'info'
    };
    return colors[type] || 'secondary';
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}>⏳</div>
        <p className={styles.loadingText}>Loading student progress...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorAlert}>
        {error}
      </div>
    );
  }

  if (!student) {
    return (
      <div className={styles.warningAlert}>
        Student not found
      </div>
    );
  }

  const allActivities = [
    ...progressData.missions.map(m => ({ ...m, type: 'mission' })),
    ...progressData.lessons.map(l => ({ ...l, type: 'lesson' })),
    ...progressData.quizzes.map(q => ({ ...q, type: 'quiz' }))
  ].sort((a, b) => new Date(b.assignedAt || b.createdAt) - new Date(a.assignedAt || a.createdAt));

  return (
    <div className={styles.unifiedStudentProgress}>
      <div className={styles.header}>
        <h3 className={styles.title}>
          {getActivityTypeIcon('mission')} {student.firstName} {student.lastName} - Progress Overview
        </h3>
        <button className={styles.closeButton} onClick={onClose}>
          Close
        </button>
      </div>

      {/* Overall Progress Summary */}
      <div className={styles.statsContainer}>
        <div className={styles.statCard}>
          <div className={styles.statCardBody}>
            <h4 className={styles.statNumber}>{progressData.overallStats.totalActivities || 0}</h4>
            <p className={styles.statLabel}>Total Activities</p>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statCardBody}>
            <h4 className={styles.statNumber}>{progressData.overallStats.completedActivities || 0}</h4>
            <p className={styles.statLabel}>Completed</p>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statCardBody}>
            <h4 className={styles.statNumber}>{progressData.overallStats.inProgressActivities || 0}</h4>
            <p className={styles.statLabel}>In Progress</p>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statCardBody}>
            <h4 className={styles.statNumber}>
              {progressData.overallStats.averageScore || 0}%
            </h4>
            <p className={styles.statLabel}>Average Score</p>
          </div>
        </div>
      </div>

      {/* Overall Progress Bar */}
      <div className={styles.progressCard}>
        <div className={styles.progressCardBody}>
          <div className={styles.progressHeader}>
            <h5 className={styles.progressTitle}>Overall Progress</h5>
            <span className={styles.progressPercentage}>{calculateOverallProgress()}% Complete</span>
          </div>
          <div className={styles.progressBarContainer}>
            <div 
              className={styles.progressBarFill}
              style={{ 
                width: `${calculateOverallProgress()}%`,
                backgroundColor: calculateOverallProgress() >= 80 ? '#28a745' : calculateOverallProgress() >= 60 ? '#ffc107' : '#dc3545'
              }}
            ></div>
          </div>
          <div className={styles.progressBreakdown}>
            <div className={styles.breakdownItem}>
              <span className={styles.breakdownLabel}>Missions:</span>
              <span className={styles.breakdownValue}>
                {progressData.missions.filter(m => m.status === 'completed').length} / {progressData.missions.length}
              </span>
            </div>
            <div className={styles.breakdownItem}>
              <span className={styles.breakdownLabel}>Lessons:</span>
              <span className={styles.breakdownValue}>
                {progressData.lessons.filter(l => l.status === 'completed').length} / {progressData.lessons.length}
              </span>
            </div>
            <div className={styles.breakdownItem}>
              <span className={styles.breakdownLabel}>Quizzes:</span>
              <span className={styles.breakdownValue}>
                {progressData.quizzes.filter(q => q.status === 'completed' || q.status === 'graded').length} / {progressData.quizzes.length}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.tabsContainer}>
        <div className={styles.tabsHeader}>
          <button 
            className={`${styles.tabButton} ${activeTab === 'overview' ? styles.tabButtonActive : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            All Activities
          </button>
          <button 
            className={`${styles.tabButton} ${activeTab === 'missions' ? styles.tabButtonActive : ''}`}
            onClick={() => setActiveTab('missions')}
          >
            Missions
          </button>
          <button 
            className={`${styles.tabButton} ${activeTab === 'lessons' ? styles.tabButtonActive : ''}`}
            onClick={() => setActiveTab('lessons')}
          >
            Lessons
          </button>
          <button 
            className={`${styles.tabButton} ${activeTab === 'quizzes' ? styles.tabButtonActive : ''}`}
            onClick={() => setActiveTab('quizzes')}
          >
            Quizzes
          </button>
        </div>
        
        <div className={styles.tabContent}>
          {activeTab === 'overview' && (
            <ActivitiesGrid 
              activities={allActivities}
              onViewDetails={(activity) => {
                setSelectedActivity(activity);
                setShowDetailModal(true);
              }}
              getActivityStatus={getActivityStatus}
              getActivityTypeIcon={getActivityTypeIcon}
              getActivityTypeColor={getActivityTypeColor}
              getGradeColor={getGradeColor}
              formatDate={formatDate}
            />
          )}
          {activeTab === 'missions' && (
            <ActivitiesGrid 
              activities={progressData.missions.map(m => ({ ...m, type: 'mission' }))}
              onViewDetails={(activity) => {
                setSelectedActivity(activity);
                setShowDetailModal(true);
              }}
              getActivityStatus={getActivityStatus}
              getActivityTypeIcon={getActivityTypeIcon}
              getActivityTypeColor={getActivityTypeColor}
              getGradeColor={getGradeColor}
              formatDate={formatDate}
            />
          )}
          {activeTab === 'lessons' && (
            <ActivitiesGrid 
              activities={progressData.lessons.map(l => ({ ...l, type: 'lesson' }))}
              onViewDetails={(activity) => {
                setSelectedActivity(activity);
                setShowDetailModal(true);
              }}
              getActivityStatus={getActivityStatus}
              getActivityTypeIcon={getActivityTypeIcon}
              getActivityTypeColor={getActivityTypeColor}
              getGradeColor={getGradeColor}
              formatDate={formatDate}
            />
          )}
          {activeTab === 'quizzes' && (
            <ActivitiesGrid 
              activities={progressData.quizzes.map(q => ({ ...q, type: 'quiz' }))}
              onViewDetails={(activity) => {
                setSelectedActivity(activity);
                setShowDetailModal(true);
              }}
              getActivityStatus={getActivityStatus}
              getActivityTypeIcon={getActivityTypeIcon}
              getActivityTypeColor={getActivityTypeColor}
              getGradeColor={getGradeColor}
              formatDate={formatDate}
            />
          )}
        </div>
      </div>

      {/* Activity Detail Modal */}
      {showDetailModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContainer}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                {selectedActivity && getActivityTypeIcon(selectedActivity.type)} {selectedActivity?.title}
              </h3>
              <button 
                className={styles.modalCloseButton}
                onClick={() => setShowDetailModal(false)}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              {selectedActivity && (
                <ActivityDetailView 
                  activity={selectedActivity}
                  quizResponseData={quizResponseData}
                  fetchQuizResponseData={fetchQuizResponseData}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ActivitiesGrid = ({ 
  activities, 
  onViewDetails, 
  getActivityStatus, 
  getActivityTypeIcon, 
  getActivityTypeColor, 
  getGradeColor, 
  formatDate 
}) => {
  if (activities.length === 0) {
    return (
      <div className={styles.emptyState}>
        <h5 className={styles.emptyStateTitle}>No activities found</h5>
        <p className={styles.emptyStateText}>This student hasn't been assigned any activities yet.</p>
      </div>
    );
  }

  return (
    <div className={styles.activitiesGrid}>
      {activities.map((activity, index) => {
        const status = getActivityStatus(activity);
        return (
          <div key={`${activity.type}-${activity._id}-${index}`} className={styles.activityCard}>
            <div className={styles.activityCardBody}>
              <div className={styles.activityCardHeader}>
                <div className={styles.activityTypeInfo}>
                  <span className={styles.activityIcon}>{getActivityTypeIcon(activity.type)}</span>
                  <span className={`${styles.activityTypeBadge} ${styles[`badge${getActivityTypeColor(activity.type)}`]}`}>
                    {activity.type}
                  </span>
                </div>
                <span className={`${styles.statusBadge} ${styles[`badge${status.variant}`]}`}>
                  {status.text}
                </span>
              </div>
              
              <h6 className={styles.activityTitle}>{activity.title}</h6>
              <p className={styles.activityDescription}>
                {activity.description}
              </p>
              
              <div className={styles.activityCardFooter}>
                <div className={styles.activityInfo}>
                  <small className={styles.infoLabel}>Score:</small>
                  {activity.percentage ? (
                    <span className={`${styles.infoValue} ${styles[getGradeColor(activity.percentage)]}`}>
                      {activity.percentage}%
                    </span>
                  ) : (
                    <span className={styles.infoValue}>-</span>
                  )}
                </div>
                
                <div className={styles.activityInfo}>
                  <small className={styles.infoLabel}>Assigned:</small>
                  <small className={styles.infoValue}>{formatDate(activity.assignedAt || activity.createdAt)}</small>
                </div>
                
                {activity.dueDate && (
                  <div className={styles.activityInfo}>
                    <small className={styles.infoLabel}>Due:</small>
                    <small className={styles.infoValue}>{formatDate(activity.dueDate)}</small>
                  </div>
                )}
                
                <button
                  className={styles.viewDetailsButton}
                  onClick={() => onViewDetails(activity)}
                >
                  View Details
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ActivityDetailView = ({ activity, quizResponseData, fetchQuizResponseData }) => {
  const [activeDetailTab, setActiveDetailTab] = useState('overview');
  
  // Fetch quiz response data when Content tab is opened for a quiz
  useEffect(() => {
    if (activeDetailTab === 'content' && activity && activity.type === 'quiz' && 
        (activity.status === 'completed' || activity.status === 'graded' || 
         activity.assignmentStatus === 'completed' || activity.assignmentStatus === 'graded')) {
      fetchQuizResponseData(activity._id);
    } else {
      // continue
    }
  }, [activeDetailTab, activity, fetchQuizResponseData]);


  return (
    <div className={styles.activityDetail}>
      <div className={styles.detailTabsContainer}>
        <div className={styles.detailTabsHeader}>
          <button 
            className={`${styles.detailTabButton} ${activeDetailTab === 'overview' ? styles.detailTabButtonActive : ''}`}
            onClick={() => setActiveDetailTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`${styles.detailTabButton} ${activeDetailTab === 'content' ? styles.detailTabButtonActive : ''}`}
            onClick={() => {
              setActiveDetailTab('content');
            }}
          >
            Content
          </button>
        </div>
        
        <div className={styles.detailTabContent}>
          {activeDetailTab === 'overview' && (
            <div className={styles.activityOverview}>
              <div className={styles.overviewGrid}>
                <div className={styles.overviewSection}>
                  <h6 className={styles.sectionTitle}>Activity Details</h6>
                  <p><strong>Title:</strong> {activity.title}</p>
                  <p><strong>Description:</strong> {activity.description}</p>
                  <p><strong>Type:</strong> {activity.type}</p>
                  <p><strong>Status:</strong> {activity.status}</p>
                </div>
                <div className={styles.overviewSection}>
                  <h6 className={styles.sectionTitle}>Progress Information</h6>
                  <p><strong>Assigned:</strong> {new Date(activity.assignedAt || activity.createdAt).toLocaleString()}</p>
                  <p><strong>Due Date:</strong> {activity.dueDate ? new Date(activity.dueDate).toLocaleString() : 'No due date'}</p>
                  <p><strong>Completed:</strong> {activity.completedAt ? new Date(activity.completedAt).toLocaleString() : 'Not completed'}</p>
                  <p><strong>Score:</strong> {activity.percentage ? `${activity.percentage}%` : 'Not graded'}</p>
                </div>
              </div>
            </div>
          )}
          
          {activeDetailTab === 'content' && (
            <div className={styles.activityContent}>
              {activity.type === 'quiz' && activity.questions && activity.questions.length > 0 ? (
                <div>
                  <p>Rendering QuizContentDisplay for quiz: {activity.title}</p>
                  <QuizContentDisplay 
                    activity={activity}
                    quizResponseData={quizResponseData}
                    fetchQuizResponseData={fetchQuizResponseData}
                  />
                </div>
              ) : activity.type === 'quiz' ? (
                <div className={styles.emptyState}>
                  <p>No quiz questions available.</p>
                </div>
              ) : (
                <div className={styles.emptyState}>
                  <p>Not a quiz activity.</p>
                </div>
              )}
              
              {activity.type === 'mission' && activity.tasks && (
                <div className={styles.missionContent}>
                  <h6 className={styles.sectionTitle}>Mission Tasks</h6>
                  {activity.tasks.map((task, index) => (
                    <div key={index} className={styles.taskItem}>
                      <h6>{task.title}</h6>
                      <p>{task.description}</p>
                      <span className={styles.taskTypeBadge}>{task.type}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
        </div>
      </div>
    </div>
  );
};

const QuizContentDisplay = ({ activity, quizResponseData, fetchQuizResponseData: _fetchQuizResponseData }) => {

  const responseData = quizResponseData[activity._id];
  const isCompleted = activity.status === 'completed' || activity.status === 'graded' ||
                     activity.assignmentStatus === 'completed' || activity.assignmentStatus === 'graded';

  const studentResponses = activity.studentResponses || responseData?.responses || {};
  const _essayResponses = activity.essayResponses || responseData?.essayResponses || {};
  const essayGrades = activity.essayGrades || responseData?.essayGrades || {};

  return (
    <div className={styles.quizContent}>
      {isCompleted ? (
        // Show student responses for completed quizzes
        <div>
          <h6 className={styles.sectionTitle}>Student Quiz Results ({activity.questions.length} questions)</h6>
          <div className={styles.quizSummary}>
                    <div className={styles.summaryItem}>
                      <strong>Score:</strong> {activity.percentage || activity.finalScore || 0}%
                    </div>
            <div className={styles.summaryItem}>
              <strong>Attempts:</strong> {activity.attempts || 0}
            </div>
            {activity.timeSpent && (
              <div className={styles.summaryItem}>
                <strong>Time Spent:</strong> {Math.floor(activity.timeSpent / 60)}m {activity.timeSpent % 60}s
              </div>
            )}
          </div>
          
                  {studentResponses && (Array.isArray(studentResponses) ? studentResponses.length > 0 : Object.keys(studentResponses).length > 0) ? (
                    // Show detailed responses if available
                    <div>
                      {activity.questions.map((question, index) => {
                        // Find the student response for this question
                        let studentAnswer = null;
                        if (Array.isArray(studentResponses)) {
                          const response = studentResponses.find(r => r.questionId.toString() === question._id.toString());
                          studentAnswer = response?.answer || null;
                        } else {
                          studentAnswer = studentResponses[question._id] || null;
                        }
                        
                        const isCorrect = studentAnswer === question.correctAnswer;
                        const pointsEarned = isCorrect ? (question.points || 1) : 0;
            
            return (
              <div key={index} className={`${styles.questionItem} ${isCorrect ? styles.correctQuestion : styles.incorrectQuestion}`}>
                <div className={styles.questionHeader}>
                  <h6>Question {index + 1} ({question.type || 'multiple-choice'})</h6>
                  <div className={styles.questionScore}>
                    {pointsEarned} / {question.points || 1} points
                  </div>
                </div>
                
                <p className={styles.questionText}>{question.question}</p>
                
                {/* Student's Answer */}
                <div className={styles.studentAnswerSection}>
                  <strong>Student's Answer:</strong>
                  <div className={`${styles.studentAnswer} ${isCorrect ? styles.correctAnswer : styles.incorrectAnswer}`}>
                                {question.type === 'essay' ? (
                                  <div className={styles.essayAnswer}>
                                    {studentAnswer || 'No answer provided'}
                                    {essayGrades && essayGrades[question._id] && (
                                      <div className={styles.essayGrade}>
                                        <strong>Grade:</strong> {essayGrades[question._id].grade}%
                                        {essayGrades[question._id].feedback && (
                                          <div className={styles.essayFeedback}>
                                            <strong>Feedback:</strong> {essayGrades[question._id].feedback}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                    ) : question.options && question.options.length > 0 ? (
                      <div>
                        {studentAnswer !== null && studentAnswer !== undefined ? (
                          <div className={styles.selectedAnswer}>
                            {(() => {
                              // If studentAnswer is a number (index), get the option text
                              if (typeof studentAnswer === 'number' && question.options[studentAnswer]) {
                                return (
                                  <div className={styles.optionItem}>
                                    {String.fromCharCode(65 + studentAnswer)}. {question.options[studentAnswer]}
                                    <span className={styles.yourAnswer}> (Your Answer)</span>
                                  </div>
                                );
                              }
                              // If studentAnswer is text, find its index
                              else if (typeof studentAnswer === 'string') {
                                const optionIndex = question.options.findIndex(opt => opt === studentAnswer);
                                if (optionIndex >= 0) {
                                  return (
                                    <div className={styles.optionItem}>
                                      {String.fromCharCode(65 + optionIndex)}. {studentAnswer}
                                      <span className={styles.yourAnswer}> (Your Answer)</span>
                                    </div>
                                  );
                                } else {
                                  return (
                                    <div className={styles.optionItem}>
                                      {studentAnswer}
                                      <span className={styles.yourAnswer}> (Your Answer)</span>
                                    </div>
                                  );
                                }
                              }
                              // Fallback
                              else {
                                return (
                                  <div className={styles.optionItem}>
                                    {studentAnswer}
                                    <span className={styles.yourAnswer}> (Your Answer)</span>
                                  </div>
                                );
                              }
                            })()}
                          </div>
                        ) : (
                          <div className={styles.noAnswer}>No answer provided</div>
                        )}
                      </div>
                    ) : (
                      <div>{studentAnswer || 'No answer provided'}</div>
                    )}
                  </div>
                </div>
                
                {/* Correct Answer (if wrong) */}
                {!isCorrect && question.correctAnswer && (
                  <div className={styles.correctAnswerSection}>
                    <strong>Correct Answer:</strong>
                    <div className={styles.correctAnswer}>
                      {question.options && question.options.length > 0 ? (
                        <div className={styles.correctOption}>
                          {(() => {
                            // If correctAnswer is a number (index), get the option text
                            if (typeof question.correctAnswer === 'number' && question.options[question.correctAnswer]) {
                              return (
                                <div>
                                  {String.fromCharCode(65 + question.correctAnswer)}. {question.options[question.correctAnswer]}
                                  <span className={styles.correctAnswerText}> ✓ (Correct)</span>
                                </div>
                              );
                            }
                            // If correctAnswer is text, find its index
                            else if (typeof question.correctAnswer === 'string') {
                              const correctIndex = question.options.findIndex(opt => opt === question.correctAnswer);
                              if (correctIndex >= 0) {
                                return (
                                  <div>
                                    {String.fromCharCode(65 + correctIndex)}. {question.correctAnswer}
                                    <span className={styles.correctAnswerText}> ✓ (Correct)</span>
                                  </div>
                                );
                              } else {
                                return (
                                  <div>
                                    {question.correctAnswer}
                                    <span className={styles.correctAnswerText}> ✓ (Correct)</span>
                                  </div>
                                );
                              }
                            }
                            // Fallback
                            else {
                              return (
                                <div>
                                  {question.correctAnswer}
                                  <span className={styles.correctAnswerText}> ✓ (Correct)</span>
                                </div>
                              );
                            }
                          })()}
                        </div>
                      ) : (
                        <div>
                          {question.correctAnswer}
                          <span className={styles.correctAnswerText}> ✓ (Correct)</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
            </div>
          ) : (
            <div className={styles.loadingState}>
              Loading student responses...
            </div>
          )}
        </div>
      ) : (
        // Show quiz questions for not completed quizzes
        <div>
          <h6 className={styles.sectionTitle}>Quiz Questions ({activity.questions.length} total)</h6>
          {activity.questions.map((question, index) => (
            <div key={index} className={styles.questionItem}>
              <h6>Question {index + 1} ({question.type || 'multiple-choice'})</h6>
              <p className={styles.questionText}>{question.question}</p>
              
              {question.options && question.options.length > 0 && (
                <div className={styles.questionOptions}>
                  <strong>Options:</strong>
                  <ul>
                    {question.options.map((option, optIndex) => (
                      <li key={optIndex} className={styles.optionItem}>
                        {String.fromCharCode(65 + optIndex)}. {option}
                        {option === question.correctAnswer && (
                          <span className={styles.correctAnswer}> ✓ (Correct)</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {question.correctAnswer && !question.options && (
                <div className={styles.correctAnswerSection}>
                  <strong>Correct Answer:</strong> {question.correctAnswer}
                </div>
              )}
              
              {question.points && (
                <div className={styles.questionPoints}>
                  <strong>Points:</strong> {question.points}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UnifiedStudentProgress;
